import { useAuth } from '@xhubio-saas/core-frontend/kernel'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Switch,
  toast,
} from '@xhubio-saas/core-frontend/ui-components'
import { Loader2, MailIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { trpc } from '@/trpc'
import { SettingsLayout } from '../../../layouts/SettingsLayout'

/**
 * E-Mail-Einstellungen (`/settings/email`).
 *
 * Ein einziger Schalter: Produkt-Neuigkeiten & Angebote. Er schreibt die
 * Einwilligung in `kernel_compliance_email_preference`
 * (`subscription_type = 'marketing'`) — **Opt-In**: ohne Zeile gibt es keine
 * Einwilligung, der Schalter steht dann auf "aus". Beim Einschalten
 * vermerkt das Backend `consent_given = true` + `consent_method =
 * 'preferences_page'`; jede Marketing-Mail trägt zusätzlich den
 * Abmelde-Link auf die öffentliche `/unsubscribe`-Seite.
 */
export function SettingsEmailPage() {
  const { t } = useTranslation()
  const { user } = useAuth()
  const utils = trpc.useUtils()

  const userId = user?.id ?? ''
  const userEmail = user?.email ?? ''

  const preferencesQuery = trpc.emailPreferences.getPreferences.useQuery(
    { userId, subscriptionTypes: ['marketing'] },
    { enabled: Boolean(userId) }
  )

  const updatePreferences = trpc.emailPreferences.updatePreferences.useMutation({
    onSuccess: async () => {
      await utils.emailPreferences.getPreferences.invalidate()
      toast.success(t('settings.email.saved', 'Email settings saved'))
    },
  })

  const marketing = preferencesQuery.data?.preferences.find(
    (pref) => pref.subscriptionType === 'marketing'
  )
  // Keine Zeile = keine Einwilligung (Opt-In).
  const isSubscribed = marketing?.subscribed === true

  const isLoading = preferencesQuery.isLoading
  const isSaving = updatePreferences.isPending

  const handleChange = (checked: boolean) => {
    if (!userId || !userEmail) return
    updatePreferences.mutate({
      userId,
      userEmail,
      preferences: [{ subscriptionType: 'marketing', subscribed: checked }],
      consentMethod: 'preferences_page',
    })
  }

  return (
    <SettingsLayout>
      <div data-testid="settings-email-page">
        <Card className="w-full max-w-xl">
          <CardHeader>
            <div className="flex gap-4">
              <MailIcon className="text-muted-foreground" />
              <CardTitle>{t('settings.email.tab', 'Email settings')}</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <div className="flex items-start justify-between gap-6">
              <div className="flex flex-col gap-1">
                <span className="text-sm font-medium">
                  {t('settings.email.marketingLabel', 'Product news & offers by email')}
                </span>
                <span className="text-sm text-muted-foreground">
                  {t(
                    'settings.email.marketingDescription',
                    'Occasional emails about new features and offers. Off by default.'
                  )}
                </span>
              </div>
              <div className="flex items-center gap-2 pt-1">
                {(isLoading || isSaving) && (
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                )}
                <Switch
                  checked={isSubscribed}
                  onCheckedChange={handleChange}
                  disabled={isLoading || isSaving || !userId}
                  aria-label={t('settings.email.marketingLabel', 'Product news & offers by email')}
                  data-testid="settings-email-marketing-switch"
                />
              </div>
            </div>

            {preferencesQuery.isError && (
              <p className="text-sm text-destructive">
                {t('settings.email.loadError', 'Email settings could not be loaded')}
              </p>
            )}

            <p className="text-xs text-muted-foreground">
              {t(
                'settings.email.legalNote',
                'Unsubscribe any time — every email contains an unsubscribe link.'
              )}
            </p>
          </CardContent>
        </Card>
      </div>
    </SettingsLayout>
  )
}
